import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  QueuedCall,
  AddToQueuePayload,
  UpdateQueuedCallPayload,
  CallQueueContextValue,
  CallQueueSettings,
  CallQueueTimerConfig,
  CALL_QUEUE_STORAGE_KEY,
  CALL_QUEUE_SETTINGS_KEY,
  MAX_CALL_AGE_MS,
  DEFAULT_URGENT_THRESHOLD_MS,
  DEFAULT_OVERDUE_THRESHOLD_MS,
  DEFAULT_PANEL_WIDTH,
} from '../types/call-queue.types';
import type { PendingQueueAction } from '../types/call-queue-api.types';
import callQueueService, { DuplicateCallError } from '../services/call-queue.service';
import {
  transformApiToLocal,
  transformLocalToApiRequest,
  transformUpdateToApiRequest,
  createTempCall,
  calculatePriority,
} from '../utils/call-queue-transformers';

// Ключи для localStorage
const PENDING_ACTIONS_KEY = 'call_queue_pending_actions';
const MIGRATION_KEY = 'call_queue_migrated_v2';

const defaultSettings: CallQueueSettings = {
  notificationsEnabled: true,
  panelWidth: DEFAULT_PANEL_WIDTH,
};

const defaultTimerConfig: CallQueueTimerConfig = {
  urgentThreshold: DEFAULT_URGENT_THRESHOLD_MS,
  overdueThreshold: DEFAULT_OVERDUE_THRESHOLD_MS,
};

const CallQueueContext = createContext<CallQueueContextValue | undefined>(undefined);

export const useCallQueue = (): CallQueueContextValue => {
  const ctx = useContext(CallQueueContext);
  if (!ctx) {
    throw new Error('useCallQueue must be used within CallQueueProvider');
  }
  return ctx;
};

export const useOptionalCallQueue = (): CallQueueContextValue | null => {
  return useContext(CallQueueContext) ?? null;
};

/**
 * Загрузка очереди из localStorage (fallback/cache)
 */
function loadQueueFromStorage(): QueuedCall[] {
  try {
    const raw = localStorage.getItem(CALL_QUEUE_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as QueuedCall[];
    const now = Date.now();
    return parsed.filter((call) => now - call.createdAt < MAX_CALL_AGE_MS);
  } catch {
    return [];
  }
}

/**
 * Сохранение очереди в localStorage (cache)
 */
function saveQueueToStorage(queue: QueuedCall[]): void {
  try {
    localStorage.setItem(CALL_QUEUE_STORAGE_KEY, JSON.stringify(queue));
  } catch {
    // Игнорируем ошибки записи
  }
}

/**
 * Загрузка настроек из localStorage
 */
function loadSettingsFromStorage(): CallQueueSettings {
  try {
    const raw = localStorage.getItem(CALL_QUEUE_SETTINGS_KEY);
    if (!raw) return defaultSettings;
    return { ...defaultSettings, ...JSON.parse(raw) };
  } catch {
    return defaultSettings;
  }
}

/**
 * Сохранение настроек в localStorage
 */
function saveSettingsToStorage(settings: CallQueueSettings): void {
  try {
    localStorage.setItem(CALL_QUEUE_SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // Игнорируем ошибки записи
  }
}

/**
 * Загрузка pending actions из localStorage
 */
function loadPendingActions(): PendingQueueAction[] {
  try {
    const raw = localStorage.getItem(PENDING_ACTIONS_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as PendingQueueAction[];
  } catch {
    return [];
  }
}

/**
 * Сохранение pending actions в localStorage
 */
function savePendingActions(actions: PendingQueueAction[]): void {
  try {
    localStorage.setItem(PENDING_ACTIONS_KEY, JSON.stringify(actions));
  } catch {
    // Игнорируем
  }
}

export const CallQueueProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  // Состояние очереди
  const [queue, setQueue] = useState<QueuedCall[]>(() => loadQueueFromStorage());
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null);

  // Состояние панели
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCardMode, setIsCardMode] = useState(false);

  // Настройки
  const [settings, setSettings] = useState<CallQueueSettings>(() => loadSettingsFromStorage());
  const timerConfig = defaultTimerConfig;

  // Состояние соединения и синхронизации
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);

  // Refs
  const queueRef = useRef<QueuedCall[]>(queue);
  const pendingActionsRef = useRef<PendingQueueAction[]>(loadPendingActions());
  const initialLoadDone = useRef(false);

  useEffect(() => {
    queueRef.current = queue;
  }, [queue]);

  // Сохранение очереди при изменениях (кэш)
  useEffect(() => {
    saveQueueToStorage(queue);
  }, [queue]);

  // Сохранение настроек при изменениях
  useEffect(() => {
    saveSettingsToStorage(settings);
  }, [settings]);

  // Отслеживание online/offline статуса
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Синхронизируем pending actions при восстановлении соединения
      syncPendingActions();
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  /**
   * Синхронизация pending actions с сервером
   */
  const syncPendingActions = useCallback(async () => {
    const actions = pendingActionsRef.current;
    if (actions.length === 0) return;

    console.log('[CallQueue] Syncing pending actions:', actions.length);
    pendingActionsRef.current = [];
    savePendingActions([]);

    for (const action of actions) {
      try {
        switch (action.type) {
          case 'add':
            if (action.payload) {
              await callQueueService.addToQueue(action.payload as any);
            }
            break;
          case 'update':
            if (action.payload) {
              await callQueueService.updateCall(action.callId, action.payload as any);
            }
            break;
          case 'remove':
            await callQueueService.removeFromQueue(action.callId);
            break;
          case 'result':
            if (action.payload) {
              await callQueueService.setResult(action.callId, action.payload as any);
            }
            break;
        }
      } catch (error) {
        console.error('[CallQueue] Failed to sync action:', action, error);
        // При ошибке 409 (дубликат) - игнорируем
        if (!(error instanceof DuplicateCallError)) {
          // Возвращаем action в очередь для повторной попытки
          pendingActionsRef.current.push(action);
        }
      }
    }

    savePendingActions(pendingActionsRef.current);

    // Запрашиваем полную синхронизацию с сервера
    await loadFromServer();
  }, []);

  /**
   * Добавление action в pending queue (для offline режима)
   */
  const addPendingAction = useCallback((action: Omit<PendingQueueAction, 'id' | 'timestamp'>) => {
    const pendingAction: PendingQueueAction = {
      ...action,
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      timestamp: Date.now(),
    };
    pendingActionsRef.current.push(pendingAction);
    savePendingActions(pendingActionsRef.current);
  }, []);

  /**
   * Загрузка очереди с сервера
   */
  const loadFromServer = useCallback(async () => {
    try {
      setIsSyncing(true);
      const serverQueue = await callQueueService.getMyQueue();
      const transformed = serverQueue.map(transformApiToLocal);
      setQueue(transformed);
      console.log('[CallQueue] Loaded from server:', transformed.length, 'items');
    } catch (error) {
      console.error('[CallQueue] Failed to load from server:', error);
      // При ошибке оставляем локальный кэш
    } finally {
      setIsSyncing(false);
    }
  }, []);

  /**
   * Миграция данных из localStorage на сервер
   */
  const migrateLocalStorageToServer = useCallback(async () => {
    if (localStorage.getItem(MIGRATION_KEY)) return;

    const localQueue = loadQueueFromStorage();
    if (localQueue.length === 0) {
      localStorage.setItem(MIGRATION_KEY, 'true');
      return;
    }

    console.log('[CallQueue] Migrating', localQueue.length, 'items to server');

    for (const call of localQueue) {
      try {
        const request = transformLocalToApiRequest({
          callId: call.callId,
          phone: call.phone,
          direction: call.direction,
          subjectType: call.subjectType,
          subjectId: call.subjectId,
          stageCode: call.stageCode,
          customerId: call.customerId,
          customerFullName: call.customerFullName,
          customerIin: call.customerIin,
          telephonyEvent: call.telephonyEvent,
        });
        await callQueueService.addToQueue(request);
      } catch (error) {
        // Игнорируем дубликаты
        if (!(error instanceof DuplicateCallError)) {
          console.error('[CallQueue] Migration failed for call:', call.callId, error);
        }
      }
    }

    localStorage.setItem(MIGRATION_KEY, 'true');
  }, []);

  // Начальная загрузка
  useEffect(() => {
    if (initialLoadDone.current) return;
    initialLoadDone.current = true;

    const init = async () => {
      // Сначала показываем из localStorage (instant UI)
      const cached = loadQueueFromStorage();
      if (cached.length > 0) {
        setQueue(cached);
      }

      // Затем загружаем с сервера
      if (navigator.onLine) {
        await migrateLocalStorageToServer();
        await loadFromServer();
      }
    };

    init();
  }, [loadFromServer, migrateLocalStorageToServer]);

  // Периодическое обновление приоритетов
  useEffect(() => {
    const interval = setInterval(() => {
      setQueue((prev) => {
        let changed = false;
        const updated = prev.map((call) => {
          if (call.status === 'completed') return call;
          const newPriority = calculatePriority(call.createdAt, timerConfig.urgentThreshold, timerConfig.overdueThreshold);
          if (newPriority !== call.priority) {
            changed = true;
            return { ...call, priority: newPriority };
          }
          return call;
        });
        return changed ? updated : prev;
      });
    }, 10000);

    return () => clearInterval(interval);
  }, [timerConfig]);

  /**
   * Добавление звонка в очередь
   */
  const addToQueue = useCallback((payload: AddToQueuePayload) => {
    // Optimistic update
    const tempCall = createTempCall(payload);
    setQueue((prev) => {
      if (prev.some((c) => c.callId === payload.callId)) {
        // Обновляем существующую карточку
        return prev.map((c) =>
          c.callId === payload.callId
            ? { ...c, ...payload, lastActivityAt: Date.now() }
            : c
        );
      }
      return [tempCall, ...prev];
    });

    // Отправляем на сервер
    if (isOnline) {
      const request = transformLocalToApiRequest(payload);
      callQueueService.addToQueue(request)
        .then((response) => {
          // Заменяем temp-карточку на серверную
          const serverCall = transformApiToLocal(response);
          setQueue((prev) =>
            prev.map((c) => (c.callId === payload.callId ? serverCall : c))
          );
        })
        .catch((error) => {
          if (error instanceof DuplicateCallError) {
            // Карточка уже существует - просто логируем
            console.log('[CallQueue] Call already exists:', payload.callId);
          } else {
            console.error('[CallQueue] Failed to add to queue:', error);
            // Добавляем в pending для повторной попытки
            addPendingAction({ type: 'add', callId: payload.callId, payload: request });
          }
        });
    } else {
      // Offline режим - сохраняем для синхронизации
      const request = transformLocalToApiRequest(payload);
      addPendingAction({ type: 'add', callId: payload.callId, payload: request });
    }
  }, [isOnline, addPendingAction]);

  /**
   * Обновление звонка в очереди
   */
  const updateCall = useCallback((callId: string, payload: UpdateQueuedCallPayload) => {
    // Optimistic update
    setQueue((prev) =>
      prev.map((call) =>
        call.callId === callId
          ? { ...call, ...payload, lastActivityAt: Date.now() }
          : call
      )
    );

    // Отправляем на сервер
    if (isOnline) {
      const request = transformUpdateToApiRequest(payload);
      callQueueService.updateCall(callId, request).catch((error) => {
        console.error('[CallQueue] Failed to update call:', error);
        addPendingAction({ type: 'update', callId, payload: request });
      });
    } else {
      const request = transformUpdateToApiRequest(payload);
      addPendingAction({ type: 'update', callId, payload: request });
    }
  }, [isOnline, addPendingAction]);

  /**
   * Удаление звонка из очереди
   */
  const removeFromQueue = useCallback((callId: string) => {
    // Optimistic update
    setQueue((prev) => prev.filter((c) => c.callId !== callId));
    setSelectedCallId((prev) => (prev === callId ? null : prev));
    setIsCardMode((prev) => {
      if (prev && selectedCallId === callId) return false;
      return prev;
    });

    // Отправляем на сервер
    if (isOnline) {
      callQueueService.removeFromQueue(callId).catch((error) => {
        console.error('[CallQueue] Failed to remove from queue:', error);
        addPendingAction({ type: 'remove', callId });
      });
    } else {
      addPendingAction({ type: 'remove', callId });
    }
  }, [isOnline, selectedCallId, addPendingAction]);

  /**
   * Отправка результата звонка
   */
  const submitResult = useCallback(async (callId: string, resultCode: string, comment?: string) => {
    const call = queueRef.current.find((c) => c.callId === callId);
    if (!call) return;

    try {
      // Отправляем результат в очередь и удаляем карточку
      if (isOnline) {
        await callQueueService.setResult(callId, { result_code: resultCode, comment });
      } else {
        addPendingAction({
          type: 'result',
          callId,
          payload: { result_code: resultCode, comment },
        });
      }

      // Удаляем карточку из локальной очереди
      setQueue((prev) => prev.filter((c) => c.callId !== callId));
      setSelectedCallId((prev) => (prev === callId ? null : prev));
      setIsCardMode((prev) => {
        if (prev && selectedCallId === callId) return false;
        return prev;
      });
    } catch (error) {
      console.error('[CallQueue] Failed to submit result:', error);
      throw error;
    }
  }, [isOnline, selectedCallId, addPendingAction]);

  /**
   * Выбор звонка
   */
  const selectCall = useCallback((callId: string | null) => {
    setSelectedCallId(callId);
    if (callId) {
      setQueue((prev) =>
        prev.map((c) => (c.callId === callId && !c.seen ? { ...c, seen: true } : c))
      );

      // Обновляем на сервере
      if (isOnline) {
        callQueueService.updateCall(callId, { seen: true }).catch(() => {
          // Игнорируем ошибки для seen
        });
      }
    }
  }, [isOnline]);

  const togglePanel = useCallback(() => {
    setIsExpanded((prev) => !prev);
    if (isCardMode) {
      setIsCardMode(false);
    }
  }, [isCardMode]);

  const expandPanel = useCallback(() => {
    setIsExpanded(true);
  }, []);

  const collapsePanel = useCallback(() => {
    setIsExpanded(false);
    setIsCardMode(false);
  }, []);

  const enterCardMode = useCallback((callId: string) => {
    setSelectedCallId(callId);
    setIsCardMode(true);
    setIsExpanded(true);
  }, []);

  const exitCardMode = useCallback(() => {
    setIsCardMode(false);
  }, []);

  const markAsSeen = useCallback((callId: string) => {
    setQueue((prev) =>
      prev.map((c) => (c.callId === callId ? { ...c, seen: true } : c))
    );

    if (isOnline) {
      callQueueService.updateCall(callId, { seen: true }).catch(() => {
        // Игнорируем
      });
    }
  }, [isOnline]);

  const reorderQueue = useCallback((fromIndex: number, toIndex: number) => {
    setQueue((prev) => {
      const result = [...prev];
      const [removed] = result.splice(fromIndex, 1);
      result.splice(toIndex, 0, removed);
      return result;
    });
  }, []);

  const updateSettings = useCallback((newSettings: Partial<CallQueueSettings>) => {
    setSettings((prev) => ({ ...prev, ...newSettings }));
  }, []);

  /**
   * Запрос полной синхронизации
   */
  const requestSync = useCallback(() => {
    if (isOnline && !isSyncing) {
      loadFromServer();
    }
  }, [isOnline, isSyncing, loadFromServer]);

  // WebSocket callbacks для внешних событий
  const onCallAdded = useCallback((call: QueuedCall) => {
    setQueue((prev) => {
      const existing = prev.find((c) => c.callId === call.callId);
      if (existing) {
        return prev.map((c) => (c.callId === call.callId ? call : c));
      }
      return [call, ...prev];
    });
  }, []);

  const onCallUpdated = useCallback((callId: string, updates: Partial<QueuedCall>) => {
    setQueue((prev) =>
      prev.map((c) => (c.callId === callId ? { ...c, ...updates } : c))
    );
  }, []);

  const onCallRemoved = useCallback((callId: string) => {
    setQueue((prev) => prev.filter((c) => c.callId !== callId));
    setSelectedCallId((prev) => (prev === callId ? null : prev));
  }, []);

  const onFullSync = useCallback((calls: QueuedCall[]) => {
    setQueue(calls);
    saveQueueToStorage(calls);
  }, []);

  // Регистрируем глобальные callbacks
  useEffect(() => {
    registerCallQueueCallbacks({
      addToQueue,
      updateCall,
      onCallAdded,
      onCallUpdated,
      onCallRemoved,
      onFullSync,
    });
    return () => {
      registerCallQueueCallbacks(null);
    };
  }, [addToQueue, updateCall, onCallAdded, onCallUpdated, onCallRemoved, onFullSync]);

  const value = useMemo<CallQueueContextValue>(
    () => ({
      queue,
      selectedCallId,
      isExpanded,
      isCardMode,
      settings,
      isOnline,
      isSyncing,
      timerConfig,
      addToQueue,
      updateCall,
      removeFromQueue,
      submitResult,
      selectCall,
      togglePanel,
      expandPanel,
      collapsePanel,
      enterCardMode,
      exitCardMode,
      markAsSeen,
      reorderQueue,
      updateSettings,
      requestSync,
    }),
    [
      queue,
      selectedCallId,
      isExpanded,
      isCardMode,
      settings,
      isOnline,
      isSyncing,
      timerConfig,
      addToQueue,
      updateCall,
      removeFromQueue,
      submitResult,
      selectCall,
      togglePanel,
      expandPanel,
      collapsePanel,
      enterCardMode,
      exitCardMode,
      markAsSeen,
      reorderQueue,
      updateSettings,
      requestSync,
    ]
  );

  return <CallQueueContext.Provider value={value}>{children}</CallQueueContext.Provider>;
};

// Тип для callbacks
export type CallQueueRef = {
  addToQueue: (payload: AddToQueuePayload) => void;
  updateCall: (callId: string, payload: UpdateQueuedCallPayload) => void;
  onCallAdded?: (call: QueuedCall) => void;
  onCallUpdated?: (callId: string, updates: Partial<QueuedCall>) => void;
  onCallRemoved?: (callId: string) => void;
  onFullSync?: (calls: QueuedCall[]) => void;
};

// Глобальный механизм регистрации callback
let globalCallQueueCallbacks: CallQueueRef | null = null;

export function registerCallQueueCallbacks(callbacks: CallQueueRef | null): void {
  globalCallQueueCallbacks = callbacks;
}

export function getCallQueueCallbacks(): CallQueueRef | null {
  return globalCallQueueCallbacks;
}
